/// <amd-module name="Dashboards/Directory/redux/FavoritesSaga" />
import { Favorite, FavoriteCreateParameters } from "Favorites/Common/Contracts/Favorite";
import { IFavoriteRestClient } from "Favorites/Common/RestClient/Favorite";
import { effects, SagaIterator } from "redux-saga";
import { ActionWithPayload } from "redux/Utils/ActionHelper";
import { IProjectData } from "Tfs/Platform/Page";
import { currentProject } from "Tfs/ReduxCommon/Modules/TfsPageData/TfsPageDataSelectors";
import { DirectoryConstants } from "VSS/Features/ReduxDirectory/Constants";
import { FavoriteStatus, IDirectoryContext } from "VSS/Features/ReduxDirectory/Contracts";
import { getFavoriteStatus } from "VSS/Features/ReduxDirectory/Redux/DirectorySelectors";
import { FavoriteActions, FavoriteActionTypes } from "VSS/Features/ReduxDirectory/Redux/Favorites/FavoriteActions";
import { IVssPageContext } from "VSS/Platform/Context";
import { traceException } from "VSS/Platform/Trace";
import { UserClaims, userHasClaim } from "VSS/Platform/UserClaims";

/**
 * Listens for actions related to favoriting directory artifacts
 */
export function* favoritesSaga(): SagaIterator {
    // Register saga to listen to favorite actions
    yield effects.takeEvery(FavoriteActionTypes.ToggleFavorite, toggleFavorite);
    yield effects.call(setFavoritesViewPermission);
}

export function* setFavoritesViewPermission(): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const hasMemberClaim: boolean = (yield effects.call(userHasClaim, pageContext, UserClaims.Member)) as any;
    yield effects.put(FavoriteActions.setFavoritesViewPermission(hasMemberClaim));
}

/**
 * Saga which is executed when the user clicks the favorite star
 * Determines which favorite serivce function to call (favorite/unfavorite)
 * @param action The toggle favorite action
 */
export function* toggleFavorite(
    action: ActionWithPayload<FavoriteActionTypes.ToggleFavorite, { artifactId: string; artifactName: string; favoriteId?: string }>
): SagaIterator {
    const { artifactId, artifactName, favoriteId } = action.payload;
    const currentFavoritingState: FavoriteStatus = (yield effects.select(getFavoriteStatus(artifactId))) as any;

    if (currentFavoritingState === FavoriteStatus.Favoriting || currentFavoritingState === FavoriteStatus.Unfavoriting) {
        return;
    } else if (!currentFavoritingState || currentFavoritingState === FavoriteStatus.Unfavorited) {
        yield effects.call(createFavorite, artifactId, artifactName);
    } else if (favoriteId) {
        yield effects.call(deleteFavorite, artifactId, favoriteId);
    }
}

/**
 * Saga which creates a new favorite
 * @param artifactId The artifact ID of the new favorite
 * @param artifactName The name of the artifact
 */
export function* createFavorite(artifactId: string, artifactName: string): SagaIterator {
    yield effects.put(FavoriteActions.beginAddFavorite(artifactId));
    const directoryContext: IDirectoryContext = (yield effects.getContext("directoryContext")) as any;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    try {
        const favoritesClient: IFavoriteRestClient = (yield effects.call([pageContext, pageContext.getRestClient], "IFavoriteRestClient")) as any;
        const project: IProjectData = (yield effects.select(currentProject)) as any;

        const favoriteParameters: FavoriteCreateParameters = {
            artifactId,
            artifactProperties: {},
            artifactName,
            artifactType: directoryContext.favoriteType,
            artifactScope: {
                id: project.id,
                type: DirectoryConstants.ARTIFACT_SCOPE_TYPE,
                name: project.name
            }
        };

        const favorite: Favorite = (yield effects.call([favoritesClient, favoritesClient.createFavorite], favoriteParameters)) as any;
        yield effects.put(
            FavoriteActions.addFavoriteSuccessful({
                id: favorite.id,
                artifactId: favorite.artifactId,
                artifactName: favorite.artifactName,
                artifactProperties: favorite.artifactProperties as { [key: string]: string },
                isDeleted: favorite.artifactIsDeleted
            })
        );
    } catch (error) {
        yield effects.put(FavoriteActions.addFavoriteFailed(artifactId, error));

        yield effects.call(traceException, pageContext, directoryContext.hubName, "Directory", error);
    }
}

/**
 * Saga which deletes a favorite
 * @param artifactId The artifact ID to delete
 * @param favoriteId The favorite ID to delete
 */
export function* deleteFavorite(artifactId: string, favoriteId: string): SagaIterator {
    yield effects.put(FavoriteActions.beginRemoveFavorite(artifactId));
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const directoryContext: IDirectoryContext = (yield effects.getContext("directoryContext")) as any;
    try {
        const favoritesClient: IFavoriteRestClient = (yield effects.call([pageContext, pageContext.getRestClient], "IFavoriteRestClient")) as any;
        const project: IProjectData = (yield effects.select(currentProject)) as any;

        yield effects.call(
            [favoritesClient, favoritesClient.deleteFavoriteById],
            favoriteId,
            directoryContext.favoriteType,
            DirectoryConstants.ARTIFACT_SCOPE_TYPE,
            project.id
        );
        yield effects.put(FavoriteActions.removeFavoriteSuccessful(artifactId));
    } catch (error) {
        yield effects.put(FavoriteActions.removeFavoriteFailed(artifactId, error));

        yield effects.call(traceException, pageContext, directoryContext.hubName, "Directory", error);
    }
}
